import { supabase } from '../supabaseClient';
import type { ClassScheduleRow, WeekdayMaxPeriod } from './sectionLayoutService';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

export interface TeamLeaderClassScheduleSectionPreviewRow {
  section_id: string;
  grade_labels: string[];
  rows: ClassScheduleRow[];
  weekday_max: WeekdayMaxPeriod;
}

export interface TeamLeaderClassSchedulePreviewPayload {
  leader_workspace_id: string;
  sections: TeamLeaderClassScheduleSectionPreviewRow[];
}

export interface TeamLeaderClassScheduleImportPayload {
  rows: ClassScheduleRow[];
  weekday_max: WeekdayMaxPeriod;
}

const DEFAULT_WEEKDAY_MAX: WeekdayMaxPeriod = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 };

function parseRows(raw: unknown): ClassScheduleRow[] {
  const source = Array.isArray(raw) ? raw : [];
  const rows = source.slice(0, 10).map((row): ClassScheduleRow => {
    const o = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const rawKind = String(o.kind ?? 'class');
    const kind: ClassScheduleRow['kind'] =
      rawKind === 'activity' || rawKind === 'after_school' ? rawKind : 'class';
    return {
      kind,
      name: String(o.name ?? ''),
      startTime: String(o.startTime ?? ''),
      durationMinutes: String(o.durationMinutes ?? ''),
      endTime: String(o.endTime ?? ''),
    };
  });
  while (rows.length < 10) {
    rows.push({ kind: 'class', name: '', startTime: '', durationMinutes: '', endTime: '' });
  }
  return rows;
}

function parseWeekdayMax(raw: unknown): WeekdayMaxPeriod {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const toPeriod = (value: unknown): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(10, Math.floor(n)));
  };
  return {
    mon: toPeriod(o.mon),
    tue: toPeriod(o.tue),
    wed: toPeriod(o.wed),
    thu: toPeriod(o.thu),
    fri: toPeriod(o.fri),
  };
}

function parseSection(raw: unknown): TeamLeaderClassScheduleSectionPreviewRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const labelsRaw = o.grade_labels;
  return {
    section_id: String(o.section_id ?? ''),
    grade_labels: Array.isArray(labelsRaw) ? labelsRaw.map((x) => String(x)) : [],
    rows: parseRows(o.rows),
    weekday_max: parseWeekdayMax(o.weekday_max ?? DEFAULT_WEEKDAY_MAX),
  };
}

export async function getTeamLeaderClassScheduleSectionsPreview(
  teamId: string
): Promise<TeamLeaderClassSchedulePreviewPayload> {
  const { data, error } = await supabase.rpc('get_team_leader_class_schedule_sections_preview', {
    p_team_id: teamId,
  });
  if (error) throw new Error(error.message);
  const raw = data as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') return { leader_workspace_id: '', sections: [] };
  const sectionsRaw = raw.sections;
  const sections = Array.isArray(sectionsRaw)
    ? sectionsRaw.map(parseSection).filter((row): row is TeamLeaderClassScheduleSectionPreviewRow => row != null)
    : [];
  return { leader_workspace_id: String(raw.leader_workspace_id ?? ''), sections };
}

export async function importTeamLeaderClassScheduleSection(
  teamId: string,
  targetWorkspaceId: string,
  sourceSectionId: string,
  targetSectionId: string
): Promise<TeamLeaderClassScheduleImportPayload> {
  const { data, error } = await supabase.rpc('import_team_class_schedule_section', {
    p_team_id: teamId,
    p_target_workspace_id: targetWorkspaceId,
    p_source_section_id: sourceSectionId,
    p_target_section_id: targetSectionId,
  });
  if (error) throw new Error(error.message);
  await invalidateSchoolScopedDataCaches(targetWorkspaceId);
  const raw = data as Record<string, unknown> | null;
  return {
    rows: parseRows(raw?.rows),
    weekday_max: parseWeekdayMax(raw?.weekday_max ?? DEFAULT_WEEKDAY_MAX),
  };
}
